import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Grid,
  Stack,
  IconButton,
  Skeleton
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { TransactionService } from '../../services';
import { useSelector } from 'react-redux';
import { selectSelectedTeam } from '../../store/authSlice';
import { fDateTime } from '../../utils';

const TransactionsTab = () => {
  const selectedTeam = useSelector(selectSelectedTeam);
  const [loading, setLoading] = useState(true);
  const [transactionsData, setTransactionsData] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const fetchTransactions = useCallback(async (page = 1) => {
    if (!selectedTeam?.teamId) {
      setTransactionsData([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const teamParams = { 
        teamId: selectedTeam?.teamId, 
        teamType: selectedTeam?.teamType,
        page: page, 
        limit: pagination.itemsPerPage
      };
      
      const response = await TransactionService.getAllTransactions(teamParams);
      
      if (response.success && response.data?.data) {
        // Use the API response data directly (don't slice it locally)
        const transactionsFromAPI = response.data.data.transactions || [];
        const totalRecords = response.data.data.totalRecord || 0;
        setTransactionsData(transactionsFromAPI);
        
        setPagination(prev => ({
          ...prev,
          currentPage: page,
          totalItems: totalRecords,
          totalPages: Math.ceil(totalRecords / prev.itemsPerPage)
        }));
      } else {
        console.log('No data in response or unsuccessful');
        setTransactionsData([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactionsData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, selectedTeam?.teamType, pagination.itemsPerPage]);

  useEffect(() => {
    if (selectedTeam?.teamId) {
      fetchTransactions(1);
    } else {
      setLoading(false);
    }
  }, [selectedTeam?.teamId, fetchTransactions]);

  const handlePageChange = (newPage) => {
    fetchTransactions(newPage);
  };

  // Skeleton component for transaction card loading state
  const TransactionCardSkeleton = () => (
    <Card 
      sx={{ 
        mb: 2,
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.08)'
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        {/* Header with Charger Name and Status */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Skeleton variant="text" width={150} height={28} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Stack>

        {/* Connector Type */}
        <Skeleton variant="text" width={120} height={20} sx={{ mb: 2 }} />

        {/* Stats Grid */}
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <Box>
              <Skeleton variant="text" width={90} height={16} />
              <Skeleton variant="text" width={60} height={24} />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <Skeleton variant="text" width={80} height={16} />
              <Skeleton variant="text" width={70} height={24} />
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box>
              <Skeleton variant="text" width={70} height={16} />
              <Skeleton variant="text" width={100} height={24} />
              <Skeleton variant="text" width={70} height={16} sx={{ mt: 1 }} />
              <Skeleton variant="text" width={100} height={24} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const TransactionsSkeleton = () => (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Skeleton variant="text" width={180} height={32} />
        </Box>
      </Box>
      
      {/* Multiple transaction card skeletons */}
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
        <TransactionCardSkeleton key={item} />
      ))}
      
      {/* Pagination skeleton */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={60} height={20} />
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return <TransactionsSkeleton />;
  }

  // Show message if no team is selected
  if (!selectedTeam?.teamId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', p: 3 }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Select a Team or Company
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Please select a team or company from the dropdown above to view transactions
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 10 }}>
      {/* Header with pagination info */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight="600" color="primary.main">
            Transactions ({pagination.totalItems})
          </Typography>
        </Box>
      </Box>

      {transactionsData.length === 0 && !loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No transactions found
          </Typography>
        </Box>
      ) : (
        transactionsData.map((txn) => (
        <Card 
          key={txn.transactionId}
          sx={{ 
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            {/* Header with Charger Name and Status */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography 
                variant="h6" 
                fontWeight="600"
                color="text.primary"
              >
                {txn?.chargepoint_id}
              </Typography>
              <Chip 
                label={txn?.stop_meter_value === null ? 'Ongoing' : 'Completed'}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  ...(txn?.stop_meter_value === null ? {
                    backgroundColor: '#2196f3',
                    color: 'white'
                  } : {
                    backgroundColor: '#e0e0e0',
                    color: 'text.secondary'
                  })
                }}
              />
            </Stack>

            {/* Connector Type */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connector: {txn?.connectorid}
            </Typography>

            {/* Stats Grid */}
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Energy Consumed
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="text.primary">
                    {Math.abs(
                      txn?.stop_meter_value - txn?.start_meter_value
                    ) / 1000 || 0} kWh
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Charging Cost
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="text.primary">
                    {txn?.totalAmountCharged}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Start Time
                  </Typography>
                  <Typography variant="body1" fontWeight="600" color="text.primary" ml={2}>
                    {fDateTime(txn?.starttime, "yyyy-MM-dd hh:mm a")}
                  </Typography>
                  { txn?.stoptime &&
                    <>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Stop Time
                    </Typography>
                    <Typography variant="body1" fontWeight="600" color="text.primary">
                      {fDateTime(txn?.stoptime, "yyyy-MM-dd hh:mm a")}
                    </Typography>
                    </>
                  }
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        ))
      )}
      
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
        {pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              size="small"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1 || loading}
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '60px', textAlign: 'center' }}>
              {pagination.currentPage} of {pagination.totalPages}
            </Typography>
            
            <IconButton 
              size="small"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages || loading}
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TransactionsTab;